import type { jsPDF } from 'jspdf';
import type { StudentContract } from '../../types/studentPortal';
import { contractKind } from './contractText';

/**
 * Renders a mounted ContractDocument to an A4 PDF, one image per contract sheet.
 * Moved unchanged from PortalContract so the portal and the public contract page
 * produce the same file.
 */
export async function renderContractPdf(root: HTMLElement): Promise<jsPDF> {
  await document.fonts.ready;
  await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
  const sheets = Array.from(root.querySelectorAll<HTMLElement>('.student-contract-sheet'));
  if (!sheets.length) throw new Error('لم يتم إنشاء صفحات العقد.');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
  for (let index = 0; index < sheets.length; index += 1) {
    const canvas = await html2canvas(sheets[index], {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: 1200,
      scrollX: 0,
      scrollY: 0,
    });
    if (index > 0) pdf.addPage('a4', 'portrait');
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.98), 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
  }
  return pdf;
}

/** The file name the portal has always used, e.g. "عقد-إيطاليا-IT-PUB-20261002-000042.pdf". */
export function contractFileName(contract: Pick<StudentContract, 'country' | 'contract_number' | 'template_key'>): string {
  const kind = contractKind(contract.template_key, contract.country.code);
  const countryName = kind === 'romania' ? 'رومانيا' : kind === 'germany' ? 'ألمانيا' : 'إيطاليا';
  return `عقد-${countryName}-${contract.contract_number}.pdf`;
}
